import { useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import type { Appointment, Staff } from '@salonomy/types';
import { cx } from '@salonomy/ui';
import { clockLabel } from '../../lib/time.js';
import { blockBox, dragSpan, gridHeight, SNAP_MIN, type DiaryGeometry } from './layout.js';

export interface ChairAccent {
  accent: string;
  soft: string;
  queue: string;
  shadow: string;
}

export interface DiaryItem {
  id: string;
  appt: Appointment;
  startMin: number;
  endMin: number;
  lane: number;
  laneCount: number;
  serviceName: string;
  pending: boolean;
}

export interface DiaryGap {
  startMin: number;
  endMin: number;
  waiting: number;
}

/** WS7: a date-specific working-hour break (lunch) that splits the column's band. */
export interface DiaryBreak {
  startMin: number;
  endMin: number;
}

export interface DiaryColumn {
  staff: Staff;
  accent: ChairAccent;
  takings: number;
  items: DiaryItem[];
  gaps: DiaryGap[];
  /** WS7 calendar realism — set when the member is off this date (greys the whole column). */
  off?: { label: string } | null;
  /**
   * WS7 — the member's adjusted working window for this date (from a custom-hours exception). When
   * present, bookable gaps are clipped to it and an out-of-band shade marks the hours they're not in.
   */
  band?: { startMin: number; endMin: number } | null;
  /** WS7 — a mid-day break stripe (from an hour-exception), in the diary's existing visual language. */
  break_?: DiaryBreak | null;
}

export interface MoveChange {
  staffId: string;
  startMin: number;
  endMin: number;
}

interface DragState {
  id: string;
  fromStaffId: string;
  startMin: number;
  endMin: number;
  originX: number;
  originY: number;
  el: HTMLElement;
  col: number;
  moved: boolean;
}

/**
 * A keyboard reschedule in progress on one appointment: the same move a pointer-drag would build,
 * but accumulated from arrow-key presses (Up/Down nudge the time, Left/Right move across chairs).
 * It is *pending* until Enter/Space commits it through the same onMove path the drop uses, or
 * Escape discards it. Only one nudge is live at a time (the focused block).
 */
interface NudgeState {
  id: string;
  /** Column index the booking currently sits in for the nudge (may differ from its origin column). */
  col: number;
  startMin: number;
  endMin: number;
}

const GUTTER_PX = 56;
const DRAG_THRESHOLD = 4;

/** The screen-reader name for a booking block: who, what, when, with whom. */
function apptLabel(item: DiaryItem, staffName: string): string {
  const who = item.appt.clientDisplayName || 'Walk-in';
  return `${who}, ${item.serviceName}, ${clockLabel(item.startMin)}–${clockLabel(item.endMin)}, ${staffName}`;
}

export function DiaryGrid({
  geometry,
  columns,
  nowMin,
  showNow,
  canManageFloor,
  closedBanner,
  onMove,
  onOpen,
  onOfferGap,
  onBlockOut,
}: {
  geometry: DiaryGeometry;
  columns: DiaryColumn[];
  nowMin: number;
  showNow: boolean;
  canManageFloor: boolean;
  /**
   * WS7: set when the salon is closed this date — banner across the diary, no bookable gaps, and
   * drag-to-move is locked (clicking a booking still opens it, but it can't be re-timed/moved).
   */
  closedBanner?: { reason: string | null } | null;
  onMove: (appointmentId: string, change: MoveChange) => void;
  onOpen: (appt: Appointment) => void;
  onOfferGap: (staffId: string, startMin: number, endMin: number) => void;
  /** WS7: quick action — block out a staff member (opens the time-off / hour-exception modal). */
  onBlockOut?: (staffId: string) => void;
}) {
  const height = gridHeight(geometry);
  const colRefs = useRef<Array<HTMLDivElement | null>>([]);
  const drag = useRef<DragState | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // WS14: keyboard reschedule. The pending nudge for the focused block (null = none), and a polite
  // aria-live message a screen reader announces ("moved to 2:15", "rescheduled", "outside booking
  // policy"). The keyboard path commits through the SAME onMove as the pointer drag — same booking-
  // policy / clash / availability guards, same optimistic update + rollback + toast.
  const [nudge, setNudge] = useState<NudgeState | null>(null);
  const [liveMessage, setLiveMessage] = useState('');
  // WS7: when the salon is closed, bookings can't be re-timed or moved from the diary at all.
  const bookingsLocked = closedBanner != null;

  const gridTemplate = `${GUTTER_PX}px repeat(${columns.length}, minmax(0, 1fr))`;

  // Hour rule lines + gutter labels.
  const hours: number[] = [];
  for (let m = Math.ceil(geometry.startMin / 60) * 60; m <= geometry.endMin; m += 60) hours.push(m);

  // Resolve which column the pointer is over, but only as a *drop target*: an off column (member on
  // time-off / closed) is never droppable, so the pointer falling over it yields -1 and the dragged
  // booking stays in its source column. This is what stops drag-to-move booking into an off slot.
  function columnAtX(clientX: number): number {
    for (let i = 0; i < colRefs.current.length; i++) {
      const el = colRefs.current[i];
      if (!el) continue;
      if (columns[i]?.off) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right) return i;
    }
    return -1;
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>, item: DiaryItem, colIndex: number) {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    drag.current = {
      id: item.id,
      fromStaffId: item.appt.staffId,
      startMin: item.startMin,
      endMin: item.endMin,
      originX: e.clientX,
      originY: e.clientY,
      el,
      col: colIndex,
      moved: false,
    };
    setDraggingId(item.id);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d) return;
    // WS7: on a closed day a booking can't be moved — swallow the move so only click-to-open remains.
    if (bookingsLocked) return;
    const dx = e.clientX - d.originX;
    const dy = e.clientY - d.originY;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;

    // Cheap: translate the single dragged element directly — no React re-render per move.
    const targetCol = columnAtX(e.clientX);
    let shiftX = 0;
    if (targetCol >= 0 && targetCol !== d.col) {
      const from = colRefs.current[d.col]?.getBoundingClientRect();
      const to = colRefs.current[targetCol]?.getBoundingClientRect();
      if (from && to) shiftX = to.left - from.left;
    }
    d.el.style.transform = `translate(${shiftX}px, ${dy}px)`;

    // Highlight the drop column directly (still no re-render).
    colRefs.current.forEach((c, i) =>
      c?.classList.toggle('diary__col--drop', i === targetCol && d.moved),
    );
  }

  function endDrag(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    drag.current = null;
    setDraggingId(null);
    colRefs.current.forEach((c) => c?.classList.remove('diary__col--drop'));
    if (!d) return;
    d.el.style.transform = '';
    try {
      d.el.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }

    if (!d.moved) {
      const item = columns[d.col]?.items.find((it) => it.id === d.id);
      if (item) onOpen(item.appt);
      return;
    }

    const dy = e.clientY - d.originY;
    const next = dragSpan({ startMin: d.startMin, endMin: d.endMin }, dy, geometry);
    const targetCol = columnAtX(e.clientX);
    const targetStaff = targetCol >= 0 ? columns[targetCol]?.staff.id : undefined;
    const staffId = targetStaff ?? d.fromStaffId;
    if (staffId === d.fromStaffId && next.startMin === d.startMin) return; // no-op
    onMove(d.id, { staffId, startMin: next.startMin, endMin: next.endMin });
  }

  // WS14 — find the next droppable column in a direction, skipping off columns (mirrors columnAtX,
  // which never resolves an off column as a drop target). Returns the same index if none is found.
  function nextDroppableCol(from: number, dir: 1 | -1): number {
    for (let i = from + dir; i >= 0 && i < columns.length; i += dir) {
      if (!columns[i]?.off) return i;
    }
    return from;
  }

  /**
   * WS14 — keyboard reschedule. Arrow Up/Down nudge the start by the diary's snap increment (keeping
   * duration); Left/Right move across staff columns. Enter/Space COMMIT through the same onMove the
   * drag-drop uses (same guards, same optimistic + rollback + toast). Escape discards the pending
   * nudge. Everything is announced via the polite aria-live region.
   */
  function onApptKeyDown(e: ReactKeyboardEvent<HTMLDivElement>, item: DiaryItem, colIndex: number) {
    // On a closed day bookings can't be re-timed, exactly like the pointer drag (which swallows moves).
    if (bookingsLocked) return;
    // The current pending nudge for this block, or its at-rest position if none is live yet.
    const base: NudgeState =
      nudge && nudge.id === item.id
        ? nudge
        : { id: item.id, col: colIndex, startMin: item.startMin, endMin: item.endMin };

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        e.preventDefault();
        const dir = e.key === 'ArrowUp' ? -1 : 1;
        // Reuse the SAME snap+clamp engine the pointer drag uses (one snap step per key press).
        const span = dragSpan(
          { startMin: base.startMin, endMin: base.endMin },
          (dir * SNAP_MIN * geometry.hourPx) / 60,
          geometry,
        );
        const next = { ...base, startMin: span.startMin, endMin: span.endMin };
        setNudge(next);
        setLiveMessage(
          `Moving to ${clockLabel(next.startMin)}–${clockLabel(next.endMin)}. Press Enter to reschedule, Escape to cancel.`,
        );
        break;
      }
      case 'ArrowLeft':
      case 'ArrowRight': {
        const dir = e.key === 'ArrowLeft' ? -1 : 1;
        const targetCol = nextDroppableCol(base.col, dir);
        if (targetCol === base.col) return; // no column that way — let the event pass through
        e.preventDefault();
        const next = { ...base, col: targetCol };
        setNudge(next);
        setLiveMessage(
          `Moving to ${columns[targetCol]?.staff.displayName ?? 'staff'}, ${clockLabel(next.startMin)}. Press Enter to reschedule, Escape to cancel.`,
        );
        break;
      }
      case 'Enter':
      case ' ': {
        if (!nudge || nudge.id !== item.id) {
          // Nothing staged → activate the block's PRIMARY action (open the appointment) — the keyboard
          // equivalent of a click. Without this, role="button" tabIndex=0 has no keyboard-reachable
          // action (open was previously only wired to pointer-up), a WCAG keyboard-operability fail.
          e.preventDefault();
          onOpen(item.appt);
          return;
        }
        e.preventDefault();
        const staffId = columns[nudge.col]?.staff.id ?? item.appt.staffId;
        const noop = staffId === item.appt.staffId && nudge.startMin === item.startMin;
        setNudge(null);
        if (noop) {
          setLiveMessage('No change.');
          return;
        }
        setLiveMessage(`Rescheduled to ${clockLabel(nudge.startMin)}.`);
        // SAME commit path as the drop: policy/clash/availability guards + optimistic + rollback.
        onMove(item.id, { staffId, startMin: nudge.startMin, endMin: nudge.endMin });
        break;
      }
      case 'Escape': {
        if (!nudge || nudge.id !== item.id) return;
        e.preventDefault();
        setNudge(null);
        setLiveMessage(`Cancelled. Back to ${clockLabel(item.startMin)}.`);
        break;
      }
      default:
        break;
    }
  }

  return (
    <div className="diary__main">
      {/* WS14: polite live region — announces a keyboard reschedule's pending change + result to a
          screen reader (e.g. "Moving to 2:15…", "Rescheduled to 2:15.", "Cancelled."). Visually
          hidden; never steals focus. */}
      <div className="slm-sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveMessage}
      </div>

      {/* WS7: salon closed this date — a banner across the diary; bookable gaps are already suppressed. */}
      {closedBanner ? (
        <div className="diary-closed" role="status">
          <span className="diary-closed__dot" aria-hidden />
          <div>
            <div className="diary-closed__title">Salon closed</div>
            <div className="diary-closed__sub">
              {closedBanner.reason ? closedBanner.reason : 'No bookings can be taken on this date.'}
            </div>
          </div>
        </div>
      ) : null}

      {/* Horizontal scroll wrapper — scrolls the whole grid as a unit on narrow viewports */}
      <div
        className="diary__scroll-wrap"
        style={{ minWidth: GUTTER_PX + 12 + columns.length * 152 }}
      >
        {/* chair headers */}
        <div className="diary__heads" style={{ gridTemplateColumns: gridTemplate }}>
          <div />
          {columns.map((col) => (
            <ChairHead key={col.staff.id} col={col} />
          ))}
        </div>

        {/* grid body */}
        <div className="diary__grid" style={{ gridTemplateColumns: gridTemplate, height }}>
          <div className="diary__gutter" style={{ height }}>
            {hours.map((m) => (
              <div
                key={m}
                className="diary__gutter-label"
                style={{ top: ((m - geometry.startMin) / 60) * geometry.hourPx }}
              >
                {clockLabel(m)}
              </div>
            ))}
          </div>

          <div className="diary__rules" style={{ height }}>
            {hours.map((m) => (
              <div
                key={m}
                className="diary__rule"
                style={{ top: ((m - geometry.startMin) / 60) * geometry.hourPx }}
              />
            ))}
          </div>

          {showNow && nowMin >= geometry.startMin && nowMin <= geometry.endMin ? (
            <div
              className="diary__now"
              style={{ top: ((nowMin - geometry.startMin) / 60) * geometry.hourPx }}
            >
              <span className="diary__now-pill">{clockLabel(nowMin)}</span>
            </div>
          ) : null}

          {columns.map((col, colIndex) => (
            <div
              key={col.staff.id}
              ref={(el) => {
                colRefs.current[colIndex] = el;
              }}
              className={cx('diary__col', col.off && 'diary__col--off')}
              style={{ height }}
            >
              {/* WS7: block-out quick action — floats top-right of each column for floor managers. */}
              {canManageFloor && onBlockOut && !col.off ? (
                <button
                  type="button"
                  className="diary__blockout"
                  title={`Block out time for ${col.staff.displayName}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onBlockOut(col.staff.id)}
                >
                  Block out…
                </button>
              ) : null}

              {/* WS7: the member is off this date — grey the column, suppress bookable gaps. */}
              {col.off ? (
                <div className="diary-off">
                  <span className="diary-off__label">{col.off.label}</span>
                  {canManageFloor && onBlockOut ? (
                    <button
                      type="button"
                      className="diary-off__edit"
                      onClick={() => onBlockOut(col.staff.id)}
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
              ) : null}

              {/* WS7: a mid-day break stripe from an hour-exception (the design's lunch treatment). */}
              {!col.off && col.break_ ? (
                <BreakStripe break_={col.break_} geometry={geometry} />
              ) : null}

              {!col.off && col.items.length === 0 && col.gaps.length === 0 ? (
                <div className="diary__empty">No bookings yet</div>
              ) : null}

              {canManageFloor && !col.off
                ? col.gaps.map((gap) => (
                    <GapBlock
                      key={`gap-${gap.startMin}`}
                      gap={gap}
                      geometry={geometry}
                      onOffer={() => onOfferGap(col.staff.id, gap.startMin, gap.endMin)}
                    />
                  ))
                : null}

              {col.items.map((item) => {
                const staged = nudge && nudge.id === item.id ? nudge : null;
                return (
                  <AppointmentBlock
                    key={item.id}
                    item={item}
                    staffName={col.staff.displayName}
                    accent={col.accent}
                    geometry={geometry}
                    dragging={draggingId === item.id}
                    // WS14: while a keyboard nudge is staged, render the block at its pending
                    // time/column so the sighted keyboard user sees what Enter will commit.
                    nudgeStartMin={staged ? staged.startMin : null}
                    nudged={staged != null && staged.col !== colIndex}
                    onPointerDown={(e) => onPointerDown(e, item, colIndex)}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={(e) => onApptKeyDown(e, item, colIndex)}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* end diary__scroll-wrap */}
      </div>
    </div>
  );
}

function ChairHead({ col }: { col: DiaryColumn }) {
  const sub =
    col.staff.engagement === 'chair_renter'
      ? 'Renter · sealed book'
      : (col.staff.title ?? 'Employed stylist');
  return (
    <div className="diary__head">
      <span
        className="slm-avatar"
        style={{ width: 34, height: 34, fontSize: 12, background: col.accent.accent }}
        aria-hidden
      >
        {initialsOf(col.staff.displayName)}
      </span>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span className="diary__head-name">{col.staff.displayName}</span>
        <span className="diary__head-sub">{sub}</span>
      </div>
      <span className="diary__head-takings">
        {col.takings ? `£${Math.round(col.takings / 100)}` : ''}
      </span>
    </div>
  );
}

function AppointmentBlock({
  item,
  staffName,
  accent,
  geometry,
  dragging,
  nudgeStartMin,
  nudged,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onKeyDown,
}: {
  item: DiaryItem;
  staffName: string;
  accent: ChairAccent;
  geometry: DiaryGeometry;
  dragging: boolean;
  /** WS14: when a keyboard nudge is staged, the pending start (block renders at this time). */
  nudgeStartMin: number | null;
  /** WS14: the staged nudge targets a different staff column (the block can't move columns visually). */
  nudged: boolean;
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => void;
}) {
  // WS14: while a vertical keyboard nudge is staged, draw the block at its pending time so the
  // sighted keyboard user previews exactly what Enter commits (duration preserved).
  const previewStart = nudgeStartMin ?? item.startMin;
  const previewSpan = {
    startMin: previewStart,
    endMin: previewStart + (item.endMin - item.startMin),
  };
  const box = blockBox(previewSpan, geometry);
  const { appt } = item;
  const inChair = appt.status === 'in_chair';
  const fromQueue =
    !inChair &&
    (appt.queueTicketId != null || appt.source === 'queue' || appt.source === 'walk_in');
  const done =
    appt.status === 'completed' || appt.status === 'cancelled' || appt.status === 'no_show';
  const tone = inChair ? 'solid' : fromQueue ? 'queue' : 'soft';

  // lane width within the column
  const widthPct = 100 / item.laneCount;
  const laneStyle =
    item.laneCount > 1
      ? {
          left: `calc(8px + ${item.lane * widthPct}%)`,
          right: 'auto',
          width: `calc(${widthPct}% - 12px)`,
        }
      : {};

  const staged = nudgeStartMin != null || nudged;
  return (
    <div
      className={cx(
        'diary-appt',
        `diary-appt--${tone}`,
        done && 'diary-appt--done',
        dragging && 'diary-appt--dragging',
        item.pending && 'diary-appt--pending',
        staged && 'diary-appt--staged',
      )}
      style={{
        top: box.top,
        height: box.height,
        ...laneStyle,
        // CSS custom properties for the accent treatment.
        ['--appt-accent' as string]: accent.accent,
        ['--appt-soft' as string]: accent.soft,
        ['--appt-queue' as string]: accent.queue,
        ['--appt-shadow' as string]: accent.shadow,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
      // WS14: accessible name + keyboard reschedule affordance for screen-reader / keyboard users.
      aria-label={apptLabel(item, staffName)}
      aria-describedby={`${item.id}-kbd-hint`}
      aria-roledescription="Appointment — use arrow keys to reschedule"
    >
      {item.pending ? <span className="diary-appt__pendingdot" /> : null}
      <span className="diary-appt__title">
        {appt.clientDisplayName || 'Walk-in'} — {item.serviceName}
      </span>
      <span className="diary-appt__meta">
        {clockLabel(item.startMin)} – {clockLabel(item.endMin)}
        {appt.priceQuoted ? ` · £${Math.round(appt.priceQuoted / 100)}` : ''}
        {fromQueue ? ' · from queue' : ''}
      </span>
      {inChair ? <span className="diary-appt__tag">In the chair</span> : null}
      {/* WS14: screen-reader-only instructions surfaced once the block is focused. */}
      <span id={`${item.id}-kbd-hint`} className="slm-sr-only">
        Press Up or Down to nudge the time, Left or Right to change staff, Enter to reschedule,
        Escape to cancel.
      </span>
    </div>
  );
}

function BreakStripe({ break_, geometry }: { break_: DiaryBreak; geometry: DiaryGeometry }) {
  const box = blockBox(break_, geometry);
  return (
    <div className="diary-break" style={{ top: box.top, height: Math.max(28, box.height) }}>
      <span>
        Break · {clockLabel(break_.startMin)} – {clockLabel(break_.endMin)}
      </span>
    </div>
  );
}

function GapBlock({
  gap,
  geometry,
  onOffer,
}: {
  gap: DiaryGap;
  geometry: DiaryGeometry;
  onOffer: () => void;
}) {
  const box = blockBox(gap, geometry);
  const mins = gap.endMin - gap.startMin;
  return (
    <div className="diary-gap" style={{ top: box.top, height: Math.max(44, box.height) }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span className="diary-gap__label">
          {clockLabel(gap.startMin)} gap · {mins} min
        </span>
        <span className="diary-gap__sub">
          {gap.waiting > 0 ? `${gap.waiting} in queue` : 'queue empty'}
        </span>
      </div>
      <button
        className="slm-btn slm-btn--primary slm-btn--sm"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onOffer}
      >
        Offer to queue
      </button>
    </div>
  );
}

function initialsOf(name: string): string {
  const t = name.trim().split(/\s+/).filter(Boolean);
  if (!t.length) return '?';
  if (t.length === 1) return t[0]!.slice(0, 2).toUpperCase();
  return (t[0]![0]! + t[t.length - 1]![0]!).toUpperCase();
}
